import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { roomService, tableService, orderService, categoryService, productService, ingredientService } from '../services/api';
import { useSocketEvents } from '../hooks/useSocket';

interface Room { id: number; name: string; }
interface DTable { id: number; room_id: number; room_name: string; name: string; seats: number; merged_into?: number | null; is_active?: number; }
interface OItem { id: number; product_id?: number; product_name: string; product_price: number; quantity: number; notes?: string; status: string; course: number; course_name?: string; }
interface Order { id: number; table_id: number; table_name: string; room_name: string; status: string; items: OItem[]; bill_requested_at?: string | null; }
interface Category { id: number; name: string; children?: Category[]; }
interface Ingredient { id: number; name: string; }
interface Product { id: number; name: string; price: number; price_unit?: string; description?: string; ingredients?: Ingredient[]; }

const statusColor: Record<string, string> = { pending: '#fbbf24', preparing: '#f97316', ready: '#22c55e', served: '#d1d5db' };
const statusLabel: Record<string, string> = { pending: 'In attesa', preparing: 'In preparazione', ready: 'Pronto', served: 'Servito' };

const slideUp = keyframes`from{transform:translateY(100%);opacity:0}to{transform:translateY(0);opacity:1}`;

// ─── Layout ────────────────────────────────────────────────────────────────────
const Page = styled.div`max-width: 1000px; margin: 0 auto; display: flex; flex-direction: column; gap: 16px;`;
const PageTitle = styled.h1`font-size: 22px; font-weight: 700; color: #111827; margin: 0;`;

// Tabs sale
const RoomTabs = styled.div`display: flex; gap: 8px; flex-wrap: wrap;`;
const RoomTab = styled.button<{ $active?: boolean }>`
  padding: 8px 20px; border-radius: 20px;
  border: 1.5px solid ${p => p.$active ? '#3b82f6' : '#e5e7eb'};
  background: ${p => p.$active ? '#3b82f6' : 'white'};
  color: ${p => p.$active ? 'white' : '#374151'};
  font-size: 14px; font-weight: 600; cursor: pointer;
`;

// Griglia tavoli
const TableGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 12px;
`;
const TableCard = styled.button<{ $status: 'free' | 'open' | 'ready' }>`
  padding: 16px 12px; border-radius: 14px; border: none; cursor: pointer;
  text-align: center; transition: transform 0.15s, box-shadow 0.15s;
  background: ${p => p.$status === 'free' ? 'white' : p.$status === 'ready' ? '#f0fdf4' : '#eff6ff'};
  border: 2px solid ${p => p.$status === 'free' ? '#e5e7eb' : p.$status === 'ready' ? '#22c55e' : '#3b82f6'};
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  &:hover { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(0,0,0,0.12); }
`;
const TNum = styled.div<{ $status: 'free' | 'open' | 'ready' }>`
  font-size: 24px; font-weight: 800;
  color: ${p => p.$status === 'free' ? '#9ca3af' : p.$status === 'ready' ? '#16a34a' : '#1d4ed8'};
  line-height: 1; margin-bottom: 6px;
`;
const TLabel = styled.div`font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: #9ca3af;`;
const TTotal = styled.div`font-size: 13px; font-weight: 700; color: #374151; margin-top: 4px;`;
const TReadyBadge = styled.div`
  display: inline-block; background: #22c55e; color: white;
  border-radius: 10px; padding: 1px 8px; font-size: 11px; font-weight: 700; margin-top: 4px;
`;
const TBillBadge = styled.div`
  display: inline-block; background: #f59e0b; color: white;
  border-radius: 10px; padding: 1px 8px; font-size: 11px; font-weight: 700; margin-top: 4px;
`;
const EmptyGrid = styled.div`padding: 40px; text-align: center; color: #9ca3af; font-size: 14px;
  background: white; border-radius: 12px; box-shadow: 0 1px 4px rgba(0,0,0,0.06);`;

// ─── Pannello ordine (drawer) ────────────────────────────────────────────────
const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.4); z-index: 200;
`;
const Drawer = styled.div`
  position: fixed; bottom: 0; left: 0; right: 0; z-index: 201;
  background: #f5f7fa;
  border-radius: 20px 20px 0 0;
  height: 94vh; max-height: 94vh;
  display: flex; flex-direction: column;
  animation: ${slideUp} 0.25s ease;
  box-shadow: 0 -8px 32px rgba(0,0,0,0.15);
  @media (min-width: 768px) { height: auto; max-height: 88vh; }
`;
const DrawerHandle = styled.div`width: 40px; height: 4px; background: #d1d5db; border-radius: 2px; margin: 12px auto 0;`;
const DrawerHeader = styled.div`
  padding: 12px 20px 14px;
  display: flex; align-items: center; justify-content: space-between;
  border-bottom: 1px solid #e5e7eb; background: white;
  border-radius: 20px 20px 0 0;
`;
const DrawerTitle = styled.div`font-size: 17px; font-weight: 700; color: #111827;`;
const HeaderBtns = styled.div`display: flex; gap: 8px;`;
const HBtn = styled.button<{ $variant?: 'danger' | 'success' | 'ghost' }>`
  padding: 7px 14px; border-radius: 8px; border: none; font-size: 13px; font-weight: 600; cursor: pointer;
  background: ${p => p.$variant === 'danger' ? '#fee2e2' : p.$variant === 'success' ? '#dcfce7' : '#f3f4f6'};
  color: ${p => p.$variant === 'danger' ? '#dc2626' : p.$variant === 'success' ? '#16a34a' : '#374151'};
  &:hover { opacity: 0.85; }
`;

const DrawerBody = styled.div`flex: 1; overflow-y: auto; display: flex; flex-direction: column; -webkit-overflow-scrolling: touch;`;

// Segmento Menu / Conto
const Segmented = styled.div`
  display: flex; gap: 6px; padding: 8px 16px; background: white;
  border-bottom: 1px solid #f3f4f6; flex-shrink: 0;
`;
const SegBtn = styled.button<{ $active?: boolean }>`
  flex: 1; padding: 11px; border-radius: 10px; border: none;
  background: ${p => p.$active ? '#111827' : '#f3f4f6'};
  color: ${p => p.$active ? 'white' : '#6b7280'};
  font-size: 15px; font-weight: 700; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 6px;
`;
const SegCount = styled.span<{ $active?: boolean }>`
  min-width: 20px; height: 20px; padding: 0 6px; border-radius: 10px;
  background: ${p => p.$active ? 'rgba(255,255,255,0.25)' : '#d1d5db'};
  color: ${p => p.$active ? 'white' : '#6b7280'};
  font-size: 12px; font-weight: 800;
  display: inline-flex; align-items: center; justify-content: center;
`;

// Riga piatto compatta (vista Menu, senza prezzo)
const MItemRow = styled.div`
  display: flex; align-items: center; gap: 8px; padding: 7px 0;
  border-bottom: 1px solid #f9fafb; &:last-child { border-bottom: none; }
`;

// Sezione ordine corrente
const OrderSection = styled.div`background: white; padding: 12px 20px; border-bottom: 1px solid #f3f4f6;`;
const SectionLabel = styled.div`font-size: 11px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 8px;`;
const ItemRow = styled.div`
  display: flex; align-items: center; gap: 8px;
  padding: 8px 0; border-bottom: 1px solid #f9fafb;
  &:last-child { border-bottom: none; }
`;
const StatusDot = styled.span<{ $c: string }>`width: 8px; height: 8px; border-radius: 50%; background: ${p => p.$c}; flex-shrink: 0;`;
const ItemName = styled.div`flex: 1; font-size: 14px; color: #1f2937; font-weight: 500;`;
const ItemNote = styled.div`font-size: 11px; color: #9ca3af; font-style: italic;`;
const ItemQty = styled.div`font-size: 13px; color: #6b7280; white-space: nowrap;`;
const ItemPrice = styled.div`font-size: 13px; font-weight: 600; color: #374151; min-width: 54px; text-align: right;`;
const RemBtn = styled.button`background: none; border: none; color: #ef4444; font-size: 16px; cursor: pointer; padding: 0 2px; opacity: 0.6; &:hover{opacity:1;}`;

// Barra portate: lista scrollabile + tasto "+ Portata" fisso a destra
const CourseBar = styled.div`
  display: flex; align-items: center; gap: 8px;
  padding: 8px 12px 8px 16px;
  background: #f9fafb; border-bottom: 1px solid #e5e7eb; flex-shrink: 0;
`;
const CourseScroll = styled.div`
  display: flex; align-items: center; gap: 6px;
  overflow-x: auto; flex: 1; min-width: 0;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;
const CourseTabBtn = styled.button<{ $active?: boolean }>`
  padding: 6px 14px; border-radius: 20px; white-space: nowrap; flex-shrink: 0;
  border: 1.5px solid ${p => p.$active ? '#cc9d6d' : '#e5e7eb'};
  background: ${p => p.$active ? '#cc9d6d' : 'white'};
  color: ${p => p.$active ? 'white' : '#6b7280'};
  font-size: 13px; font-weight: 600; cursor: pointer;
`;
const AddCourseTabBtn = styled.button`
  padding: 8px 14px; border-radius: 20px; white-space: nowrap; flex-shrink: 0;
  border: 1.5px solid #cc9d6d; background: #fff7ed;
  color: #cc9d6d; font-size: 13px; font-weight: 700; cursor: pointer;
  &:active { background: #cc9d6d; color: white; }
`;

const CourseHeader = styled.div<{ $active?: boolean }>`
  display: flex; align-items: center; gap: 8px; padding: 8px 0 6px;
  border-bottom: 2px solid ${p => p.$active ? '#cc9d6d' : '#e5e7eb'};
  margin-bottom: 6px;
`;
const CourseNum = styled.div<{ $active?: boolean }>`
  width: 24px; height: 24px; border-radius: 50%; flex-shrink: 0;
  background: ${p => p.$active ? '#cc9d6d' : '#e5e7eb'};
  color: ${p => p.$active ? 'white' : '#6b7280'};
  font-size: 12px; font-weight: 800;
  display: flex; align-items: center; justify-content: center;
`;
const SendBar = styled.div`
  padding: 12px 20px; background: white; border-top: 1px solid #f3f4f6;
`;
const SendBtn = styled.button`
  width: 100%; padding: 14px; border-radius: 12px; border: none;
  background: #16a34a; color: white; font-size: 15px; font-weight: 700;
  cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px;
  &:hover { background: #15803d; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const DraftLabel = styled.span`
  font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;
  color: #f97316; background: #fff7ed; border-radius: 4px; padding: 1px 5px;
`;

const TotalBar = styled.div`
  padding: 10px 20px; background: white;
  display: flex; justify-content: space-between; align-items: center;
  border-top: 1px solid #f3f4f6;
`;
const TotalTxt = styled.span`font-size: 15px; font-weight: 700; color: #111827;`;

// Sezione menu
const MenuSection = styled.div`padding: 12px 20px; flex: 1;`;
const MenuSearchWrap = styled.div`position: relative; margin-bottom: 10px;`;
const ClearSearchBtn = styled.button`
  position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
  background: #f3f4f6; border: none; border-radius: 50%; width: 22px; height: 22px;
  color: #6b7280; font-size: 12px; cursor: pointer; line-height: 1;
  display: flex; align-items: center; justify-content: center;
  &:hover { background: #e5e7eb; }
`;
const CatGrid = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 12px;
`;
const CatChipBtn = styled.button<{ $active?: boolean }>`
  padding: 7px 14px; border-radius: 20px;
  border: 1.5px solid ${p => p.$active ? '#cc9d6d' : '#e5e7eb'};
  background: ${p => p.$active ? '#cc9d6d' : 'white'};
  color: ${p => p.$active ? 'white' : '#374151'};
  font-size: 13px; font-weight: 600; cursor: pointer; white-space: nowrap;
`;
const ProductList = styled.div`display: flex; flex-direction: column; gap: 4px;`;
const ProdRow = styled.div`
  display: flex; align-items: center; gap: 8px;
  padding: 12px 14px; border-radius: 12px;
  background: white; border: 1px solid #e5e7eb;
`;
const ProdName = styled.div`font-size: 15px; font-weight: 600; color: #1f2937; flex: 1;`;
const QtyWrap = styled.div`display: flex; align-items: center; gap: 6px; flex-shrink: 0;`;
const QtyCircle = styled.button<{ $type?: 'add' | 'remove' }>`
  width: 42px; height: 42px; border-radius: 50%; border: none; flex-shrink: 0;
  background: ${p => p.$type === 'add' ? '#cc9d6d' : '#f3f4f6'};
  color: ${p => p.$type === 'add' ? 'white' : '#374151'};
  font-size: 22px; font-weight: 700; cursor: pointer; line-height: 1;
  display: flex; align-items: center; justify-content: center;
  -webkit-tap-highlight-color: transparent;
  &:active { transform: scale(0.92); }
`;
const QtyBadge = styled.div`
  min-width: 28px; text-align: center;
  font-size: 17px; font-weight: 800; color: #1f2937;
`;
const ModBtn = styled.button`
  padding: 9px 12px; border-radius: 20px; border: 1px solid #e5e7eb;
  background: white; color: #9ca3af; font-size: 12px; font-weight: 600;
  cursor: pointer; flex-shrink: 0; white-space: nowrap;
  &:active { background: #f5f3ff; border-color: #818cf8; color: #818cf8; }
`;

const ModalQtyRow = styled.div`
  display: flex; align-items: center; gap: 12px;
  background: #f9fafb; border-radius: 10px; padding: 10px 14px;
  margin-bottom: 14px;
`;
const ModalQtyLabel = styled.div`font-size: 13px; color: #6b7280; flex: 1;`;
const ModalQtyBtn = styled.button`
  width: 32px; height: 32px; border-radius: 50%; border: 1px solid #d1d5db;
  background: white; font-size: 18px; font-weight: 700; cursor: pointer; color: #374151;
  display: flex; align-items: center; justify-content: center;
  &:hover { border-color: #cc9d6d; color: #cc9d6d; }
`;
const ModalQtyNum = styled.div`
  font-size: 18px; font-weight: 800; color: #1f2937; min-width: 28px; text-align: center;
`;

// Modal unione tavoli
const MergeModal = styled.div`
  position: fixed; inset: 0; z-index: 300;
  display: flex; align-items: flex-end; justify-content: center;
  background: rgba(0,0,0,0.5);
`;
const MergeBox = styled.div`
  background: white; border-radius: 20px 20px 0 0;
  padding: 20px; width: 100%; max-width: 480px;
  max-height: 60vh; overflow-y: auto;
`;
const MergeTitle = styled.div`font-size: 15px; font-weight: 700; color: #111827; margin-bottom: 4px;`;
const MergeSub = styled.div`font-size: 13px; color: #6b7280; margin-bottom: 14px;`;
const MergeGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(100px, 1fr)); gap: 10px;`;
const MergeTableBtn = styled.button`
  padding: 14px 8px; border-radius: 12px; border: 2px solid #e5e7eb;
  background: white; cursor: pointer; text-align: center;
  font-size: 18px; font-weight: 800; color: #1f2937;
  &:hover { border-color: #3b82f6; background: #eff6ff; color: #1d4ed8; }
`;
const MergeCancel = styled.button`
  width: 100%; margin-top: 14px; padding: 11px;
  border-radius: 10px; border: none; background: #f3f4f6;
  color: #374151; font-size: 14px; font-weight: 600; cursor: pointer;
`;

const MergedTag = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  background: #eff6ff; color: #1d4ed8; border-radius: 20px;
  padding: 2px 10px; font-size: 12px; font-weight: 600;
  border: 1px solid #bfdbfe;
`;
const UnmergeX = styled.button`
  background: none; border: none; color: #1d4ed8; cursor: pointer;
  font-size: 13px; padding: 0 2px; line-height: 1;
  &:hover { color: #dc2626; }
`;

// Modal ingredienti
const NoteModal = styled.div`
  position: fixed; inset: 0; z-index: 300;
  display: flex; align-items: flex-end; justify-content: center;
  background: rgba(0,0,0,0.5);
`;
const NoteBox = styled.div`
  background: white; border-radius: 20px 20px 0 0; padding: 20px;
  width: 100%; max-width: 480px; max-height: 80vh; overflow-y: auto;
`;
const NoteTitle = styled.div`font-size: 16px; font-weight: 700; color: #111827; margin-bottom: 2px;`;
const NotePrice = styled.div`font-size: 13px; color: #cc9d6d; font-weight: 600; margin-bottom: 14px;`;
const IngSection = styled.div`margin-bottom: 14px;`;
const IngLabel = styled.div`font-size: 11px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 8px;`;
const IngChips = styled.div`display: flex; flex-wrap: wrap; gap: 8px;`;
const IngChip = styled.button<{ $removed?: boolean }>`
  padding: 6px 14px; border-radius: 20px; font-size: 13px; font-weight: 500; cursor: pointer;
  border: 1.5px solid ${p => p.$removed ? '#fca5a5' : '#d1fae5'};
  background: ${p => p.$removed ? '#fef2f2' : '#f0fdf4'};
  color: ${p => p.$removed ? '#dc2626' : '#16a34a'};
  text-decoration: ${p => p.$removed ? 'line-through' : 'none'};
  transition: all 0.15s;
`;
const SearchInput = styled.input`
  width: 100%; padding: 8px 12px; border: 1.5px solid #e5e7eb; border-radius: 8px;
  font-size: 14px; box-sizing: border-box;
  &:focus { outline: none; border-color: #818cf8; }
`;
const SearchResults = styled.div`
  border: 1px solid #e5e7eb; border-radius: 8px; margin-top: 4px;
  max-height: 160px; overflow-y: auto; background: white;
`;
const SearchResult = styled.button`
  width: 100%; text-align: left; padding: 9px 12px; border: none; background: white;
  font-size: 14px; color: #1f2937; cursor: pointer; border-bottom: 1px solid #f9fafb;
  &:last-child { border-bottom: none; }
  &:hover { background: #eef2ff; color: #4f46e5; }
`;
const AddedChips = styled.div`display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px;`;
const AddedChip = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 10px; border-radius: 20px;
  background: #eef2ff; color: #4f46e5;
  border: 1px solid #c7d2fe; font-size: 13px; font-weight: 500;
`;
const ChipX = styled.button`
  background: none; border: none; color: #818cf8; cursor: pointer;
  font-size: 14px; padding: 0; line-height: 1;
  &:hover { color: #dc2626; }
`;
const ExtraSection = styled.div`margin-bottom: 4px;`;
const NoteArea = styled.textarea`
  width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px;
  font-size: 14px; resize: none; font-family: inherit; box-sizing: border-box;
  &:focus { outline: none; border-color: #cc9d6d; }
`;
const NoteActions = styled.div`display: flex; gap: 8px; margin-top: 14px;`;
const NBtn = styled.button<{ $primary?: boolean }>`
  flex: 1; padding: 12px; border-radius: 10px; border: none; font-size: 14px; font-weight: 600; cursor: pointer;
  background: ${p => p.$primary ? '#cc9d6d' : '#f3f4f6'};
  color: ${p => p.$primary ? 'white' : '#374151'};
`;

// ─── Componente ────────────────────────────────────────────────────────────────

export default function TableOrder() {
  const { on } = useSocketEvents();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [tables, setTables] = useState<DTable[]>([]);
  const [activeOrders, setActiveOrders] = useState<Order[]>([]);
  const [activeRoomId, setActiveRoomId] = useState<number | null>(null);
  const [selectedTable, setSelectedTable] = useState<DTable | null>(null);
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [activeCatId, setActiveCatId] = useState<number | null>(null);
  const [allIngredients, setAllIngredients] = useState<Ingredient[]>([]);
  const [pendingProduct, setPendingProduct] = useState<Product | null>(null);
  const [removedIngredients, setRemovedIngredients] = useState<Set<number>>(new Set());
  const [addedExtras, setAddedExtras] = useState<Ingredient[]>([]);
  const [extraSearch, setExtraSearch] = useState('');
  const [modalQty, setModalQty] = useState(1);
  const [extraNote, setExtraNote] = useState('');
  const [activeCourse, setActiveCourse] = useState(1);
  const [courseNames, setCourseNames] = useState<Record<number, string>>({});
  const [showMerge, setShowMerge] = useState(false);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [menuSearch, setMenuSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [drawerTab, setDrawerTab] = useState<'menu' | 'conto'>('menu');

  // Ref al corpo scrollabile del drawer (per riportare in alto sulla portata dopo un'aggiunta da ricerca)
  const drawerBodyRef = useRef<HTMLDivElement>(null);
  // Ref sempre aggiornato con l'ordine corrente (per letture nei timer/handler ottimistici)
  const orderRef = useRef<Order | null>(null);
  useEffect(() => { orderRef.current = currentOrder; }, [currentOrder]);
  // Timer di sync quantità (debounce per tap ripetuti su +/−)
  const qtyTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  // Numero di modifiche ottimistiche in volo: finché >0 evitiamo reload che causerebbero flicker
  const pendingSync = useRef(0);

  const reloadOrders = useCallback(async () => {
    const res = await orderService.getActive();
    setActiveOrders(res.data?.orders || []);
  }, []);

  const reloadCurrentOrder = useCallback(async (orderId: number) => {
    // Non sovrascrivere lo stato ottimistico mentre ci sono sync in volo
    if (pendingSync.current > 0) return;
    const res = await orderService.getById(orderId);
    if (res.data?.order) setCurrentOrder(res.data.order);
  }, []);

  // Applica una modifica locale immediata alle voci dell'ordine corrente
  const patchOrderItems = useCallback((fn: (items: OItem[]) => OItem[]) => {
    setCurrentOrder(o => (o ? { ...o, items: fn(o.items) } : o));
  }, []);

  // Invia al server la quantità aggiornata, accorpando tap ravvicinati
  const scheduleQtySync = useCallback((itemId: number) => {
    pendingSync.current++;
    if (qtyTimers.current[itemId]) clearTimeout(qtyTimers.current[itemId]);
    qtyTimers.current[itemId] = setTimeout(async () => {
      delete qtyTimers.current[itemId];
      const it = orderRef.current?.items.find(i => i.id === itemId);
      try {
        if (it && it.id > 0) await orderService.updateItemQty(it.id, it.quantity);
      } finally {
        pendingSync.current = Math.max(0, pendingSync.current - 1);
        reloadOrders();
      }
    }, 350);
  }, [reloadOrders]);

  useEffect(() => {
    const init = async () => {
      const [r, t, cat, ord, ings] = await Promise.all([
        roomService.getAll(), tableService.getAll(),
        categoryService.getPublic(), orderService.getActive(),
        ingredientService.getAll(),
      ]);
      setAllIngredients(ings.data?.ingredients || ings.ingredients || []);
      const roomList = r.data?.rooms || [];
      setRooms(roomList);
      setTables(t.data?.tables || []);
      setCategories(cat?.data?.categories || []);
      setActiveOrders(ord.data?.orders || []);
      if (roomList.length > 0) setActiveRoomId(roomList[0].id);
      // Carica prodotti prima categoria
      const cats = cat?.data?.categories || [];
      if (cats.length > 0) {
        setActiveCatId(cats[0].id);
        const p = await productService.getByCategory(cats[0].id);
        setProducts(p.data?.products || []);
      }
    };
    init().finally(() => setLoading(false));
  }, []);

  const reloadTables = useCallback(async () => {
    const t = await tableService.getAll();
    setTables(t.data?.tables || []);
  }, []);

  useEffect(() => {
    const cleanups = [
      on('tables:updated', () => reloadTables()),
      on('order:new', () => reloadOrders()),
      on('order:updated', (d: any) => {
        reloadOrders();
        if (currentOrder && d?.order?.id === currentOrder.id && d.order.status !== 'open') {
          setCurrentOrder(null); setSelectedTable(null);
        }
      }),
      on('order:item_added', (d: any) => { reloadOrders(); if (currentOrder && d?.order_id === currentOrder.id) reloadCurrentOrder(currentOrder.id); }),
      on('order:item_updated', () => { reloadOrders(); if (currentOrder) reloadCurrentOrder(currentOrder.id); }),
      on('order:item_removed', () => { reloadOrders(); if (currentOrder) reloadCurrentOrder(currentOrder.id); }),
    ];
    return () => cleanups.forEach(fn => fn());
  }, [on, currentOrder, reloadOrders, reloadCurrentOrder, reloadTables]);

  // Ricerca prodotto globale (tutte le categorie), con debounce
  useEffect(() => {
    const q = menuSearch.trim();
    if (q.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await productService.search(q);
        setSearchResults(res.data?.products || res.products || []);
      } catch { setSearchResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [menuSearch]);

  const handleSelectCategory = async (catId: number) => {
    setMenuSearch('');
    setActiveCatId(catId);
    const res = await productService.getByCategory(catId);
    setProducts(res.data?.products || []);
  };

  const handleSelectTable = async (table: DTable) => {
    setSelectedTable(table);
    setActiveCourse(1);
    setCourseNames({});
    setMenuSearch('');
    setDrawerTab('menu');
    const existing = activeOrders.find(o => o.table_id === table.id && o.status === 'open');
    if (existing) {
      const full = await orderService.getById(existing.id);
      const ord = full.data?.order || null;
      setCurrentOrder(ord);
      // Ripristina nomi portate dall'ordine esistente
      if (ord?.items?.length) {
        const names: Record<number, string> = {};
        for (const item of ord.items) {
          if (item.course_name) names[item.course] = item.course_name;
        }
        setCourseNames(names);
        const maxCourse = Math.max(...ord.items.map((i: OItem) => i.course));
        setActiveCourse(maxCourse);
      }
    } else {
      setCurrentOrder(null);
    }
  };

  const handleOpenOrder = async () => {
    if (!selectedTable) return;
    const res = await orderService.create({ table_id: selectedTable.id });
    if (res.success) {
      setCurrentOrder({ ...res.data.order, items: [] });
      reloadOrders();
    } else if (res.data?.order_id) {
      const full = await orderService.getById(res.data.order_id);
      setCurrentOrder(full.data?.order || null);
    }
  };

  const handleMerge = async (secondaryTable: DTable) => {
    if (!selectedTable || !currentOrder) return;
    const res = await tableService.merge(selectedTable.id, secondaryTable.id);
    if (res.success) {
      setShowMerge(false);
      await reloadTables();
    } else {
      alert(res.message || 'Errore unione tavoli');
    }
  };

  const handleUnmerge = async (secondaryId: number) => {
    await tableService.unmerge(secondaryId);
    await reloadTables();
  };

  // Articoli draft nella portata attiva
  const draftCourseItems = (currentOrder?.items || []).filter(
    i => i.course === activeCourse && i.status === 'draft'
  );

  // Legge le voci draft della portata attiva dallo stato più recente (ref)
  const currentDraftItems = () =>
    (orderRef.current?.items || []).filter(i => i.course === activeCourse && i.status === 'draft');

  const getCourseQty = (productId: number) =>
    draftCourseItems.filter(i => i.product_id === productId).reduce((s, i) => s + i.quantity, 0);

  const handleQuickAdd = async (product: Product) => {
    const order = orderRef.current;
    if (!order) return;
    const existing = currentDraftItems().find(i => i.product_id === product.id && !i.notes);
    if (existing) {
      // Incremento ottimistico + sync accorpato
      patchOrderItems(items => items.map(i => i.id === existing.id ? { ...i, quantity: i.quantity + 1 } : i));
      scheduleQtySync(existing.id);
    } else {
      // Inserimento ottimistico con id temporaneo, poi rimpiazzato dalla risposta
      const tempId = -Date.now();
      patchOrderItems(items => [...items, {
        id: tempId, product_id: product.id, product_name: product.name,
        product_price: product.price, quantity: 1, status: 'draft',
        course: activeCourse, course_name: courseNames[activeCourse] || '',
      }]);
      pendingSync.current++;
      try {
        const res = await orderService.addItem(order.id, {
          product_id: product.id, product_name: product.name,
          product_price: product.price, quantity: 1,
          course: activeCourse, course_name: courseNames[activeCourse] || '',
        });
        if (res.success && res.data?.item) {
          patchOrderItems(items => items.map(i => i.id === tempId ? res.data.item : i));
        }
      } finally {
        pendingSync.current = Math.max(0, pendingSync.current - 1);
        reloadOrders();
      }
    }
  };

  const handleQuickRemove = async (product: Product) => {
    const order = orderRef.current;
    if (!order) return;
    // Rimuovi prima le versioni senza note, poi quelle con note
    const items = currentDraftItems().filter(i => i.product_id === product.id);
    const target = items.find(i => !i.notes) || items[0];
    if (!target) return;
    if (target.quantity > 1) {
      patchOrderItems(its => its.map(i => i.id === target.id ? { ...i, quantity: i.quantity - 1 } : i));
      scheduleQtySync(target.id);
    } else {
      patchOrderItems(its => its.filter(i => i.id !== target.id));
      if (target.id > 0) {
        pendingSync.current++;
        try { await orderService.removeItem(target.id); }
        finally { pendingSync.current = Math.max(0, pendingSync.current - 1); reloadOrders(); }
      }
    }
  };

  const handleProductClick = (product: Product) => {
    setPendingProduct(product);
    setRemovedIngredients(new Set());
    setAddedExtras([]);
    setExtraSearch('');
    setExtraNote('');
    setModalQty(1);
  };

  const toggleRemove = (id: number) => {
    setRemovedIngredients(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const buildNote = (): string | undefined => {
    const parts: string[] = [];
    if (pendingProduct?.ingredients && removedIngredients.size > 0) {
      const names = pendingProduct.ingredients
        .filter(i => removedIngredients.has(i.id))
        .map(i => i.name).join(', ');
      if (names) parts.push(`Senza: ${names}`);
    }
    if (addedExtras.length > 0) {
      parts.push(`Extra: ${addedExtras.map(i => i.name).join(', ')}`);
    }
    if (extraNote.trim()) parts.push(extraNote.trim());
    return parts.length > 0 ? parts.join(' · ') : undefined;
  };

  const handleConfirmAdd = async () => {
    if (!currentOrder || !pendingProduct) return;
    await orderService.addItem(currentOrder.id, {
      product_id: pendingProduct.id,
      product_name: pendingProduct.name,
      product_price: pendingProduct.price,
      quantity: modalQty,
      notes: buildNote(),
      course: activeCourse,
      course_name: courseNames[activeCourse] || '',
    });
    setPendingProduct(null);
    if (menuSearch) {
      setMenuSearch('');
      drawerBodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
    await reloadCurrentOrder(currentOrder.id);
    reloadOrders();
  };

  const handleSendOrder = async () => {
    if (!currentOrder) return;
    setSending(true);
    try {
      await orderService.sendOrder(currentOrder.id);
      await reloadCurrentOrder(currentOrder.id);
      reloadOrders();
    } finally {
      setSending(false);
    }
  };

  const hasDraftItems = (currentOrder?.items || []).some(i => i.status === 'draft');

  const handleRemoveItem = async (itemId: number) => {
    if (!currentOrder) return;
    await orderService.removeItem(itemId);
    await reloadCurrentOrder(currentOrder.id);
    reloadOrders();
  };

  const handleRequestBill = async () => {
    if (!currentOrder) return;
    await orderService.requestBill(currentOrder.id);
    await reloadCurrentOrder(currentOrder.id);
    reloadOrders();
  };

  const getTableStatus = (table: DTable): 'free' | 'open' | 'ready' => {
    const order = activeOrders.find(o => o.table_id === table.id && o.status === 'open');
    if (!order) return 'free';
    if (order.items.some(i => i.status === 'ready')) return 'ready';
    return 'open';
  };

  // Tavoli della sala corrente, esclusi quelli uniti ad altri o disattivati
  const roomTables = tables.filter(t => t.room_id === activeRoomId && !t.merged_into && t.is_active !== 0);
  // Tavoli uniti al tavolo selezionato corrente
  const mergedIntoSelected = selectedTable ? tables.filter(t => t.merged_into === selectedTable.id) : [];
  // Tavoli disponibili per l'unione (stessa sala, liberi, non già uniti, attivi)
  const mergeableTablesForRoom = tables.filter(t =>
    t.id !== selectedTable?.id &&
    !t.merged_into &&
    t.is_active !== 0 &&
    !activeOrders.some(o => o.table_id === t.id && o.status === 'open')
  );
  const total = currentOrder?.items.reduce((s, i) => s + i.product_price * i.quantity, 0) ?? 0;

  if (loading) return <Page><p style={{ color: '#6b7280' }}>Caricamento...</p></Page>;

  return (
    <Page>
      <PageTitle>Ordini Tavoli</PageTitle>

      {/* Selezione sala */}
      <RoomTabs>
        {rooms.map(r => (
          <RoomTab key={r.id} $active={r.id === activeRoomId} onClick={() => setActiveRoomId(r.id)}>
            {r.name}
          </RoomTab>
        ))}
      </RoomTabs>

      {/* Griglia tavoli */}
      {roomTables.length === 0 ? (
        <EmptyGrid>Nessun tavolo in questa sala.<br/>Aggiungili da <strong>Sale & Tavoli</strong>.</EmptyGrid>
      ) : (
        <TableGrid>
          {roomTables.map(table => {
            const status = getTableStatus(table);
            const order = activeOrders.find(o => o.table_id === table.id && o.status === 'open');
            const itemCount = order?.items.reduce((s, i) => s + i.quantity, 0) ?? 0;
            const readyCount = order?.items.filter(i => i.status === 'ready').length ?? 0;
            return (
              <TableCard key={table.id} $status={status} onClick={() => handleSelectTable(table)}>
                <TNum $status={status}>{table.name}</TNum>
                <TLabel>{status === 'free' ? 'Libero' : 'Aperto'}</TLabel>
                {order && itemCount > 0 && <TTotal>{itemCount} {itemCount === 1 ? 'piatto' : 'piatti'}</TTotal>}
                {readyCount > 0 && <TReadyBadge>✓ {readyCount} pronto</TReadyBadge>}
                {order?.bill_requested_at && <TBillBadge>💰 Conto</TBillBadge>}
              </TableCard>
            );
          })}
        </TableGrid>
      )}

      {/* Drawer ordine */}
      {selectedTable && (
        <>
          <Overlay onClick={() => { setSelectedTable(null); setCurrentOrder(null); }} />
          <Drawer>
            <DrawerHandle />
            <DrawerHeader>
              <div style={{ flex: 1 }}>
                <DrawerTitle style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {selectedTable.room_name} — Tavolo {selectedTable.name}
                  {mergedIntoSelected.map(t => (
                    <MergedTag key={t.id}>
                      + Tavolo {t.name}
                      <UnmergeX title="Dividi tavolo" onClick={() => handleUnmerge(t.id)}>✕</UnmergeX>
                    </MergedTag>
                  ))}
                </DrawerTitle>
              </div>
              <HeaderBtns>
                {currentOrder && mergeableTablesForRoom.length > 0 && (
                  <HBtn title="Unisci con un altro tavolo" onClick={() => setShowMerge(true)}>⊕ Unisci</HBtn>
                )}
                <HBtn onClick={() => { setSelectedTable(null); setCurrentOrder(null); setShowMerge(false); }}>✕</HBtn>
              </HeaderBtns>
            </DrawerHeader>

            {/* Schede Menu / Conto */}
            {currentOrder && (
              <Segmented>
                <SegBtn $active={drawerTab === 'menu'} onClick={() => setDrawerTab('menu')}>Menu</SegBtn>
                <SegBtn $active={drawerTab === 'conto'} onClick={() => setDrawerTab('conto')}>
                  Conto
                  {currentOrder.items.length > 0 && <SegCount $active={drawerTab === 'conto'}>{currentOrder.items.length}</SegCount>}
                </SegBtn>
              </Segmented>
            )}

            {/* Barra portate — solo nella scheda Menu. Tab scorrono, "+ Portata" sempre visibile */}
            {currentOrder && drawerTab === 'menu' && (() => {
              const existingCourses = Array.from(new Set(currentOrder.items.map(i => i.course))).sort((a, b) => a - b);
              if (!existingCourses.includes(activeCourse)) existingCourses.push(activeCourse);
              return (
                <CourseBar>
                  <CourseScroll>
                    {existingCourses.map(n => (
                      <CourseTabBtn key={n} $active={activeCourse === n} onClick={() => setActiveCourse(n)}>
                        Portata {n}
                      </CourseTabBtn>
                    ))}
                  </CourseScroll>
                  <AddCourseTabBtn onClick={() => {
                    const next = Math.max(...existingCourses) + 1;
                    setActiveCourse(next);
                  }}>+ Portata</AddCourseTabBtn>
                </CourseBar>
              );
            })()}

            <DrawerBody ref={drawerBodyRef}>
              {/* Tavolo libero */}
              {!currentOrder ? (
                <OrderSection>
                  <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 12 }}>Tavolo libero</div>
                    <HBtn $variant="success" onClick={handleOpenOrder} style={{ fontSize: 14, padding: '10px 24px' }}>
                      Apri ordine
                    </HBtn>
                  </div>
                </OrderSection>
              ) : drawerTab === 'menu' ? (
                <>
                  {/* Portata attiva: piatti aggiunti (senza prezzo) */}
                  <OrderSection>
                    {(() => {
                      const courseItems = currentOrder.items.filter(i => i.course === activeCourse);
                      if (courseItems.length === 0) {
                        return <div style={{ fontSize: 13, color: '#9ca3af', padding: '4px 0' }}>Portata {activeCourse}: nessun piatto. Aggiungili dal menu qui sotto.</div>;
                      }
                      return courseItems.map(item => (
                        <MItemRow key={item.id}>
                          <StatusDot $c={item.status === 'draft' ? '#d1d5db' : (statusColor[item.status] || '#d1d5db')} />
                          <ItemQty style={{ fontWeight: 800, color: '#1f2937' }}>{item.quantity}×</ItemQty>
                          <div style={{ flex: 1 }}>
                            <ItemName style={{ opacity: item.status === 'draft' ? 0.75 : 1 }}>
                              {item.product_name}
                              {item.status === 'draft' && <DraftLabel style={{ marginLeft: 6 }}>bozza</DraftLabel>}
                            </ItemName>
                            {item.notes && <ItemNote>📝 {item.notes}</ItemNote>}
                          </div>
                          {item.status === 'draft' && (
                            <RemBtn onClick={() => handleRemoveItem(item.id)} style={{ fontSize: 20, padding: '0 6px' }}>✕</RemBtn>
                          )}
                        </MItemRow>
                      ));
                    })()}
                  </OrderSection>

                  {/* Menu per aggiungere piatti */}
                  <MenuSection>
                    <SectionLabel>Aggiungi piatti</SectionLabel>
                    <MenuSearchWrap>
                      <SearchInput
                        placeholder="🔍 Cerca un piatto in tutto il menu…"
                        value={menuSearch}
                        onChange={e => setMenuSearch(e.target.value)}
                      />
                      {menuSearch && <ClearSearchBtn onClick={() => setMenuSearch('')}>✕</ClearSearchBtn>}
                    </MenuSearchWrap>
                    {!menuSearch && (
                      <CatGrid>
                        {categories.map(cat => (
                          <CatChipBtn key={cat.id} $active={activeCatId === cat.id} onClick={() => handleSelectCategory(cat.id)}>
                            {cat.name}
                          </CatChipBtn>
                        ))}
                      </CatGrid>
                    )}
                    <ProductList>
                      {menuSearch.trim().length >= 2 && searchResults.length === 0 && (
                        <div style={{ padding: '12px 4px', color: '#9ca3af', fontSize: 13 }}>Nessun piatto trovato.</div>
                      )}
                      {(() => {
                        const inSearch = menuSearch.trim().length >= 2;
                        return (inSearch ? searchResults : products)
                          .filter((p: any) => p.is_active !== 0 && p.is_available !== 0).map((product: any) => {
                          const qty = getCourseQty(product.id);
                          return (
                            <ProdRow key={product.id}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <ProdName>{product.name}</ProdName>
                                {inSearch && product.category_name && (
                                  <div style={{ fontSize: 11, color: '#9ca3af' }}>{product.category_name}</div>
                                )}
                              </div>
                              <QtyWrap>
                                {qty > 0 && (
                                  <>
                                    <QtyCircle $type="remove" onClick={() => handleQuickRemove(product)}>−</QtyCircle>
                                    <QtyBadge>{qty}</QtyBadge>
                                  </>
                                )}
                                <QtyCircle $type="add" onClick={async () => {
                                  await handleQuickAdd(product);
                                  if (inSearch) {
                                    setMenuSearch('');
                                    drawerBodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
                                  }
                                }}>+</QtyCircle>
                                <ModBtn onClick={() => handleProductClick(product)}>variante</ModBtn>
                              </QtyWrap>
                            </ProdRow>
                          );
                        });
                      })()}
                    </ProductList>
                  </MenuSection>
                </>
              ) : (
                /* Scheda Conto — riepilogo con prezzi */
                <>
                  {(() => {
                    const allCourses = Array.from(new Set(currentOrder.items.map(i => i.course))).sort((a, b) => a - b);
                    if (allCourses.length === 0) {
                      return <OrderSection><div style={{ fontSize: 14, color: '#9ca3af', textAlign: 'center', padding: '16px 0' }}>Ordine vuoto.</div></OrderSection>;
                    }
                    return (
                      <OrderSection>
                        {allCourses.map(courseNum => {
                          const items = currentOrder.items.filter(i => i.course === courseNum);
                          const name = courseNames[courseNum] || `Portata ${courseNum}`;
                          return (
                            <div key={courseNum} style={{ marginBottom: 8 }}>
                              <CourseHeader>
                                <CourseNum>{courseNum}</CourseNum>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>{name}</div>
                              </CourseHeader>
                              {items.map(item => (
                                <ItemRow key={item.id}>
                                  <StatusDot $c={item.status === 'draft' ? '#d1d5db' : (statusColor[item.status] || '#d1d5db')} />
                                  <div style={{ flex: 1 }}>
                                    <ItemName>{item.product_name}</ItemName>
                                    {item.notes && <ItemNote>📝 {item.notes}</ItemNote>}
                                  </div>
                                  <ItemQty>×{item.quantity}</ItemQty>
                                  <ItemPrice>€ {(item.product_price * item.quantity).toFixed(2)}</ItemPrice>
                                </ItemRow>
                              ))}
                            </div>
                          );
                        })}
                      </OrderSection>
                    );
                  })()}
                  <TotalBar>
                    <TotalTxt>Totale</TotalTxt>
                    <TotalTxt>€ {total.toFixed(2)}</TotalTxt>
                  </TotalBar>
                  <div style={{ padding: '12px 20px' }}>
                    {currentOrder.bill_requested_at ? (
                      <div style={{
                        textAlign: 'center', padding: '13px', borderRadius: 10,
                        background: '#ecfdf5', color: '#047857', fontSize: 15, fontWeight: 700,
                        border: '1.5px solid #a7f3d0',
                      }}>
                        ✓ Conto richiesto — in attesa della cassa
                      </div>
                    ) : (
                      <SendBtn onClick={handleRequestBill} disabled={currentOrder.items.length === 0}>
                        💰 Richiedi conto alla cassa
                      </SendBtn>
                    )}
                  </div>
                </>
              )}
            </DrawerBody>

            {/* Invia comanda — solo scheda Menu */}
            {currentOrder && drawerTab === 'menu' && hasDraftItems && (
              <SendBar>
                <SendBtn onClick={handleSendOrder} disabled={sending}>
                  {sending ? 'Invio in corso…' : (() => {
                    const n = currentOrder.items.filter(i => i.status === 'draft').reduce((s, i) => s + i.quantity, 0);
                    return `✓ Invia comanda${n > 0 ? ` (${n})` : ''}`;
                  })()}
                </SendBtn>
              </SendBar>
            )}
          </Drawer>
        </>
      )}

      {/* Modal unione tavoli */}
      {showMerge && selectedTable && (
        <MergeModal onClick={() => setShowMerge(false)}>
          <MergeBox onClick={e => e.stopPropagation()}>
            <MergeTitle>Unisci con un altro tavolo</MergeTitle>
            <MergeSub>
              Seleziona il tavolo da unire al Tavolo {selectedTable.name}. Il tavolo scelto scomparirà dalla griglia e tornerà libero quando chiudi il conto.
            </MergeSub>
            <MergeGrid>
              {mergeableTablesForRoom.map(t => (
                <MergeTableBtn key={t.id} onClick={() => handleMerge(t)}>
                  <div>{t.name}</div>
                  <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400, marginTop: 2 }}>{t.room_name}</div>
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>{t.seats} posti</div>
                </MergeTableBtn>
              ))}
            </MergeGrid>
            <MergeCancel onClick={() => setShowMerge(false)}>Annulla</MergeCancel>
          </MergeBox>
        </MergeModal>
      )}

      {/* Modal ingredienti */}
      {pendingProduct && (
        <NoteModal onClick={() => setPendingProduct(null)}>
          <NoteBox onClick={e => e.stopPropagation()}>
            <NoteTitle>{pendingProduct.name} — variante</NoteTitle>

            <ModalQtyRow>
              <ModalQtyLabel>Quante porzioni con queste variazioni?</ModalQtyLabel>
              <ModalQtyBtn onClick={() => setModalQty(q => Math.max(1, q - 1))}>−</ModalQtyBtn>
              <ModalQtyNum>{modalQty}</ModalQtyNum>
              <ModalQtyBtn onClick={() => setModalQty(q => q + 1)}>+</ModalQtyBtn>
            </ModalQtyRow>

            {pendingProduct.ingredients && pendingProduct.ingredients.length > 0 && (
              <IngSection>
                <IngLabel>Ingredienti — tocca per rimuovere</IngLabel>
                <IngChips>
                  {pendingProduct.ingredients.map(ing => (
                    <IngChip
                      key={ing.id}
                      $removed={removedIngredients.has(ing.id)}
                      onClick={() => toggleRemove(ing.id)}
                    >
                      {removedIngredients.has(ing.id) ? '✕ ' : '✓ '}{ing.name}
                    </IngChip>
                  ))}
                </IngChips>
              </IngSection>
            )}

            {(() => {
              const dishIngIds = new Set((pendingProduct.ingredients || []).map(i => i.id));
              const addedIds = new Set(addedExtras.map(i => i.id));
              const searchResults = extraSearch.trim()
                ? allIngredients.filter(i =>
                    !dishIngIds.has(i.id) &&
                    !addedIds.has(i.id) &&
                    i.name.toLowerCase().includes(extraSearch.toLowerCase())
                  ).slice(0, 8)
                : [];
              return (
                <IngSection>
                  <IngLabel>Aggiunte</IngLabel>
                  <SearchInput
                    placeholder="Cerca ingrediente da aggiungere…"
                    value={extraSearch}
                    onChange={e => setExtraSearch(e.target.value)}
                  />
                  {searchResults.length > 0 && (
                    <SearchResults>
                      {searchResults.map(ing => (
                        <SearchResult
                          key={ing.id}
                          type="button"
                          onClick={() => {
                            setAddedExtras(prev => [...prev, ing]);
                            setExtraSearch('');
                          }}
                        >
                          + {ing.name}
                        </SearchResult>
                      ))}
                    </SearchResults>
                  )}
                  {addedExtras.length > 0 && (
                    <AddedChips>
                      {addedExtras.map(ing => (
                        <AddedChip key={ing.id}>
                          {ing.name}
                          <ChipX type="button" onClick={() => setAddedExtras(prev => prev.filter(i => i.id !== ing.id))}>✕</ChipX>
                        </AddedChip>
                      ))}
                    </AddedChips>
                  )}
                </IngSection>
              );
            })()}

            <ExtraSection>
              <IngLabel>Nota extra (opzionale)</IngLabel>
              <NoteArea
                rows={2}
                placeholder="Es: ben cotto, senza glutine, extra piccante…"
                value={extraNote}
                onChange={e => setExtraNote(e.target.value)}
              />
            </ExtraSection>

            <NoteActions>
              <NBtn onClick={() => setPendingProduct(null)}>Annulla</NBtn>
              <NBtn $primary onClick={handleConfirmAdd}>Aggiungi</NBtn>
            </NoteActions>
          </NoteBox>
        </NoteModal>
      )}
    </Page>
  );
}
